'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import BookingForm from '@/components/BookingForm';
import { Shield, Clock, MapPin, Headphones, CalendarCheck, MessageCircle, Phone, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { appSettings } from '@/app-settings';

function BookingContent() {
  const searchParams = useSearchParams();
  const vehicleId = searchParams.get('vehicle') || '';

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Layered background */}
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.15),transparent)]" />

        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Floating decorative elements */}
        <div className="absolute top-20 left-[10%] w-64 h-64 rounded-full bg-primary/5 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-[15%] w-48 h-48 rounded-full bg-primary/10 blur-2xl" />

        {/* Decorative shapes */}
        <div className="absolute top-32 right-[20%] w-3 h-3 rounded-full bg-primary/60" />
        <div className="absolute top-40 right-[25%] w-2 h-2 rounded-full bg-white/30" />
        <div className="absolute bottom-20 left-[18%] w-2 h-2 rounded-full bg-primary/40" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center">
            {/* Badge */}
            <Badge variant="outline" className="gap-2 mb-6 border-primary/20 bg-primary/10 text-primary">
              <CalendarCheck className="w-4 h-4" />
              <span>Quick & Easy Booking</span>
            </Badge>

            <h1 className="text-5xl lg:text-6xl font-display font-bold text-foreground mb-4 tracking-tight">
              Book Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-red-400 to-orange-400">
                Adventure
              </span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose your perfect ride and explore Sri Lanka on two wheels.
              Best prices, full insurance, and 24/7 support included.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Content */}
      <section className="py-12 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <BookingForm selectedVehicle={vehicleId} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Why Book With Us */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4 text-card-foreground">Why Book With Us</h3>
                  <div className="space-y-4">
                    {[
                      { icon: Shield, title: 'Full Insurance', desc: 'Comprehensive coverage included' },
                      { icon: Clock, title: '24/7 Support', desc: 'Help whenever you need it' },
                      { icon: MapPin, title: 'Free Delivery', desc: 'To Negombo locations' },
                      { icon: Headphones, title: 'Easy Booking', desc: 'Quick confirmation via WhatsApp' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <item.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-card-foreground">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Need Help */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2 text-card-foreground">Need Help?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our team is available 24/7 to assist you with your booking.
                  </p>
                  <div className="space-y-3">
                    <Button variant="success" className="w-full gap-2" asChild>
                      <a
                        href={`https://wa.me/${appSettings.contactPhoneFull}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat on WhatsApp
                      </a>
                    </Button>
                    <div className="space-y-2 text-sm">
                      <a
                        href={`tel:+${appSettings.contactPhoneFull}`}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Phone className="w-4 h-4" /> {appSettings.contactPhoneFormatted}
                      </a>
                      <a
                        href="mailto:info@srilankabikerent.com"
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Mail className="w-4 h-4" /> info@srilankabikerent.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Note */}
              <Card className="border-primary/20 bg-primary/10">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2 text-primary">Pricing Note</h3>
                  <p className="text-sm text-muted-foreground">
                    Prices shown are daily rates. Weekly and monthly discounts available.
                    Delivery charges apply for locations outside Negombo.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function BookPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background pt-24 pb-20 flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    }>
      <BookingContent />
    </Suspense>
  );
}
